import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import EventModal from "./EventModal";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "short", year: "numeric", month: "short", day: "numeric",
  });
};

const fmtTime = (t) => {
  if (!t) return "—";
  // Accept "HH:MM" or "HH:MM:SS"
  const [h, m] = t.split(":");
  const d = new Date();
  d.setHours(Number(h), Number(m || 0), 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const RSVP_LABEL = { yes: "Yes", no: "No", maybe: "Maybe" };

/**
 * CalendarList — default landing page listing events from `eventcal`.
 * Selector: ACTIVE (default, upcoming from today), THIS_YEAR, ALL.
 * Rows are sorted by date ascending. Row click opens the detail modal
 * (edit + delete). "New event" button opens the create modal.
 * Every authenticated user may create / edit / delete events.
 */
export default function CalendarList({ profile, canWrite, canAdmin }) {
  const [view, setView] = useState("active");
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null);   // { mode:"create"|"edit", row }
  const [reloadKey, setReloadKey] = useState(0);
  const [rsvpBusy, setRsvpBusy] = useState(null);      // eventcal_seq in flight
  const [expanded, setExpanded] = useState(null);      // eventcal_seq showing respondents
  const [respondents, setRespondents] = useState({});  // seq -> rows

  useEffect(() => {
    let ignore = false;
    setErr(""); setRows(null);
    api.listEventcal(view)
      .then((r) => { if (!ignore) setRows(r || []); })
      .catch((e) => {
        if (!ignore) setErr(e?.response?.data?.detail || e.message || "Failed to load events");
      });
    return () => { ignore = true; };
  }, [view, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  const castRsvp = async (seq, response) => {
    setRsvpBusy(seq);
    try {
      await api.setEventcalRsvp(seq, response);
      refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "RSVP failed");
    } finally {
      setRsvpBusy(null);
    }
  };

  const toggleRespondents = async (seq) => {
    if (expanded === seq) { setExpanded(null); return; }
    setExpanded(seq);
    if (!respondents[seq]) {
      try {
        const list = await api.getEventcalRespondents(seq);
        setRespondents((m) => ({ ...m, [seq]: list }));
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || "Failed to load respondents");
      }
    }
  };

  const total = rows?.length ?? 0;
  const todayIso = new Date().toISOString().slice(0, 10);
  const viewLabel = useMemo(() => ({
    active: "ACTIVE", this_year: "THIS_YEAR", all: "ALL",
  }[view] || "ACTIVE"), [view]);

  return (
    <section data-testid="calendar-section">
      <p className="section-title">Events</p>
      <div className="card list-card txn-card" data-testid="calendar-card">
        <div className="list-header txn-header">
          <div className="list-header-left txn-header-left">
            <label className="budget-label" htmlFor="cal-view-select">View</label>
            <select
              id="cal-view-select"
              className="fy-select"
              data-testid="calendar-view-select"
              value={view}
              onChange={(e) => setView(e.target.value)}
              title="Event view"
              style={{ marginRight: 12 }}
            >
              <option value="active">ACTIVE</option>
              <option value="this_year">THIS_YEAR</option>
              <option value="all">ALL</option>
            </select>
            <span className="list-count count" data-testid="calendar-count">
              {rows == null
                ? "Loading…"
                : `${total} event${total === 1 ? "" : "s"} · ${viewLabel}`}
            </span>
          </div>
          <div className="list-header-right txn-header-right">
            {canWrite && (
              <button className="btn-navy"
                      data-testid="calendar-new-btn"
                      onClick={() => setModal({ mode: "create", row: {} })}
              >+ New event</button>
            )}
          </div>
        </div>

        <div className="list-scroll txn-scroll" data-testid="calendar-scroll">
          {err && <div className="form-error" style={{ margin: "14px 18px 0" }}>{err}</div>}
          {rows == null ? (
            <div className="loading-row"><span className="spinner" /> Loading events…</div>
          ) : rows.length === 0 ? (
            <div className="empty" data-testid="calendar-empty">
              <span className="big" aria-hidden>∅</span>
              No events for this view.
            </div>
          ) : (
            <table className="list-table list-table-compact txn-table txn-table-compact calendar-table"
                   data-testid="calendar-table">
              <thead>
                <tr className="list-head-labels txn-head-labels">
                  <th style={{ width: "18%" }}>Date</th>
                  <th style={{ width: "11%" }}>From</th>
                  <th style={{ width: "11%" }}>To</th>
                  <th>Title</th>
                  <th style={{ width: "26%" }}>RSVP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const seq = r.eventcal_seq;
                  const mine = r.my_rsvp;
                  const busy = rsvpBusy === seq;
                  const respondentRows = respondents[seq] || [];
                  const isFutureEvent = r.eventcal_dt >= todayIso;
                  return (
                    <React.Fragment key={seq}>
                      <tr
                        data-testid={`calendar-row-${seq}`}
                        onClick={() => setModal({ mode: "edit", row: r })}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{fmtDate(r.eventcal_dt)}</td>
                        <td className="muted">{fmtTime(r.eventcal_frm)}</td>
                        <td className="muted">{fmtTime(r.eventcal_end)}</td>
                        <td>
                          <b>{r.eventcal_ttl}</b>
                          {r.series_id && r.series_total > 1 && (
                            <span className="series-badge" data-testid={`calendar-series-badge-${seq}`}>
                              {r.series_position} of {r.series_total}
                            </span>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="rsvp-group">
                            {!isFutureEvent ? (
                              <span className="muted rsvp-closed" data-testid={`calendar-rsvp-closed-${seq}`}>
                                Yes {r.rsvp_yes ?? 0} · No {r.rsvp_no ?? 0} · Maybe {r.rsvp_maybe ?? 0}
                              </span>
                            ) : mine ? (
                              <span
                                className={`rsvp-badge rsvp-${mine}`}
                                data-testid={`calendar-rsvp-badge-${seq}`}
                              >{RSVP_LABEL[mine]} {r[`rsvp_${mine}`] ?? 0}</span>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  className="rsvp-pill rsvp-yes"
                                  data-testid={`calendar-rsvp-yes-${seq}`}
                                  disabled={busy}
                                  onClick={() => castRsvp(seq, "yes")}
                                >Yes {r.rsvp_yes ?? 0}</button>
                                <button
                                  type="button"
                                  className="rsvp-pill rsvp-no"
                                  data-testid={`calendar-rsvp-no-${seq}`}
                                  disabled={busy}
                                  onClick={() => castRsvp(seq, "no")}
                                >No {r.rsvp_no ?? 0}</button>
                                <button
                                  type="button"
                                  className="rsvp-pill rsvp-maybe"
                                  data-testid={`calendar-rsvp-maybe-${seq}`}
                                  disabled={busy}
                                  onClick={() => castRsvp(seq, "maybe")}
                                >Maybe {r.rsvp_maybe ?? 0}</button>
                              </>
                            )}
                            <button
                              type="button"
                              className="signup-btn"
                              data-testid={`calendar-signup-${seq}`}
                              disabled={!r.eventcal_url}
                              style={r.eventcal_url ? undefined : { visibility: "hidden", pointerEvents: "none" }}
                              title={r.eventcal_url ? "Open sign-up link in a new tab" : ""}
                              onClick={() => { if (r.eventcal_url) window.open(r.eventcal_url, "_blank", "noopener,noreferrer"); }}
                            >SignUp</button>
                            {canAdmin && (
                              <button
                                type="button"
                                className="rsvp-who"
                                data-testid={`calendar-rsvp-who-${seq}`}
                                onClick={() => toggleRespondents(seq)}
                              >{expanded === seq ? "Hide" : "Who?"}</button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {r.eventcal_dsc && (
                        <tr
                          className="calendar-desc-row"
                          data-testid={`calendar-desc-${seq}`}
                          onClick={() => setModal({ mode: "edit", row: r })}
                          style={{ cursor: "pointer" }}
                        >
                          <td></td>
                          <td colSpan={4}>{r.eventcal_dsc}</td>
                        </tr>
                      )}
                      {canAdmin && expanded === seq ? (
                        <tr className="calendar-spacer" aria-hidden="false">
                          <td colSpan={5}>
                            <div className="rsvp-detail" data-testid={`calendar-rsvp-detail-${seq}`}>
                              {respondentRows.length === 0
                                ? "No responses yet."
                                : ["yes", "no", "maybe"].map((k) => {
                                    const names = respondentRows
                                      .filter((p) => p.response === k)
                                      .map((p) => `${p.first_name || ""} ${p.last_name || ""}`.trim())
                                      .join(", ");
                                    return names ? (
                                      <span key={k} className={`rsvp-detail-group rsvp-${k}`}>
                                        <b>{k[0].toUpperCase() + k.slice(1)}:</b> {names}
                                      </span>
                                    ) : null;
                                  })}
                            </div>
                          </td>
                        </tr>
                      ) : idx < rows.length - 1 && (
                        <tr className="calendar-spacer" aria-hidden="true">
                          <td colSpan={5}>&nbsp;</td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {modal && (
        <EventModal
          mode={modal.mode}
          row={modal.row}
          canWrite={canWrite}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); refresh(); }}
          onDeleted={() => { setModal(null); refresh(); }}
        />
      )}
    </section>
  );
}
